import { PropsWithChildren, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import type { Content } from '@plone/types';
import type { BlockWrapperDataProps } from '../BlockWrapper/BlockWrapper';
import { BlockWrapper } from '../BlockWrapper/BlockWrapper';
import { messages } from './messages';
import calculateKeyData from './KeyData';

import { Info } from '@designsystem/stories/02-atoms/Info';
import { USPs } from '@designsystem/stories/03-molecules/USPs';

interface ViewProps {
  data: BlockWrapperDataProps;
  content: Content;
  isEdit?: boolean;
  block: string;
  onChangeBlock: (blockId: string, newData: any) => void;
}

const Factory = (props: PropsWithChildren) => <>{props.children}</>;

const View = (props: ViewProps) => {
  const intl = useIntl();
  const _content = useSelector(
    (state: { content: { data: Content } }) => state.content.data,
  );
  const content =
    props?.content && (props.content?.['@id'] || props.content?.parent)
      ? props.content
      : _content;
  const data = { ...props?.data };
  const location = useSelector(
    (state: { router: { location: { pathname: string; search: string } } }) =>
      state?.router?.location,
  );
  const type =
    content?.['@type'] ||
    (location.pathname.endsWith('/add')
      ? location.search.match(/type=([^&]+)/)?.[1]
      : '');

  let updated = false;
  data.block_compact = true;

  if (!data?.block_title) {
    switch (type) {
      case 'DegreeProgram':
      case 'ContinuingEducation':
        data.block_title = intl.formatMessage(messages.Factsheet);
        break;
      case 'Project':
        data.block_title = intl.formatMessage(messages.ProjectDetails);
        break;
      default:
        data.block_title = intl.formatMessage(messages.KeyData);
    }
    updated = true;
  }

  useEffect(() => {
    if (updated && props?.isEdit && props.onChangeBlock) {
      props.onChangeBlock(props.block, data);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const info = calculateKeyData(content, intl);
  const bulletpoints =
    content?.bulletpoints && content?.bulletpoints.map((val) => val?.textline);

  return (
    <>
      {(info || props?.isEdit) && (
        <BlockWrapper
          isEdit={props?.isEdit}
          block={props?.block}
          onChangeBlock={props?.onChangeBlock}
          data={data}
          factory={Factory}
        >
          <Info type="extended" items={info} />
        </BlockWrapper>
      )}

      {bulletpoints && bulletpoints.length > 0 && (
        <BlockWrapper
          data={{ block_followup: true }}
          factory={Factory}
          block={props?.block}
          onChangeBlock={props?.onChangeBlock}
        >
          <USPs
            title={intl.formatMessage(messages.USPsTitle)}
            items={bulletpoints as string[]}
          />
        </BlockWrapper>
      )}
    </>
  );
};

export default View;
